// Interface para mensagem de chat
export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

// Interface para histórico de conversas
export interface Conversation {
  id: string;
  customerName: string;
  customerPhone: string;
  lastMessage: string;
  timestamp: Date;
  messages: ChatMessage[];
  status: 'active' | 'closed';
}

// Interface para o status do bot
export interface BotStatus {
  status: 'stopped' | 'running';
  lastModified: Date;
}

// Interface para as configurações do bot
export interface BotSettings {
  model: string;
  temperature: number;
  maxTokens: number;
  personalityType: string;
  language: string;
  autoLearn: boolean;
  useContext: boolean;
  useKnowledgeBase: boolean;
}

// Interface para configuração de captura automática de leads
export interface AutoLeadCaptureSettings {
  enabled: boolean;
  autoCreateLead: boolean;
  captureProfilePic: boolean;
  notifyNewContacts: boolean;
}

const STORAGE_KEY = 'whatsapp_conversations';
const BOT_STATUS_KEY = 'whatsapp_bot_status';
const BOT_SETTINGS_KEY = 'whatsapp_bot_settings';
const AUTO_LEAD_CAPTURE_KEY = 'whatsapp_auto_lead_capture';
const PRODUCT_INTEGRATION_KEY = 'whatsapp_product_integration';

// Carrega o histórico de conversas do localStorage
export const loadConversationHistory = (): Conversation[] => {
  try {
    const savedConversations = localStorage.getItem(STORAGE_KEY);
    if (savedConversations) {
      // Precisamos converter as strings de data de volta para objetos Date
      const parsedConversations = JSON.parse(savedConversations);
      return parsedConversations.map((conversation: any) => ({
        ...conversation,
        timestamp: new Date(conversation.timestamp),
        messages: conversation.messages.map((message: any) => ({
          ...message,
          timestamp: new Date(message.timestamp)
        }))
      }));
    }
    return [];
  } catch (error) {
    console.error('Erro ao carregar histórico de conversas:', error);
    return [];
  }
};

// Salva o histórico de conversas no localStorage
export const saveConversationHistory = (conversations: Conversation[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  } catch (error) {
    console.error('Erro ao salvar histórico de conversas:', error);
    throw new Error('Erro ao salvar histórico de conversas');
  }
};

// Carrega o status do bot do localStorage
export const loadBotStatus = (): { status: 'stopped' | 'running' } => {
  try {
    const savedStatus = localStorage.getItem(BOT_STATUS_KEY);
    if (savedStatus) {
      const parsedStatus = JSON.parse(savedStatus);
      return {
        status: parsedStatus.status
      };
    }
    return { status: 'stopped' };
  } catch (error) {
    console.error('Erro ao carregar status do bot:', error);
    return { status: 'stopped' };
  }
};

// Salva o status do bot no localStorage
export const saveBotStatus = (status: 'stopped' | 'running'): void => {
  try {
    localStorage.setItem(BOT_STATUS_KEY, JSON.stringify({
      status,
      lastModified: new Date()
    }));
  } catch (error) {
    console.error('Erro ao salvar status do bot:', error);
    throw new Error('Erro ao salvar status do bot');
  }
};

// Carrega as configurações do bot do localStorage
export const loadBotSettings = (): BotSettings => {
  try {
    const savedSettings = localStorage.getItem(BOT_SETTINGS_KEY);
    if (savedSettings) {
      return JSON.parse(savedSettings);
    }
    return {
      model: 'gpt-4',
      temperature: 0.7,
      maxTokens: 150,
      personalityType: 'professional',
      language: 'portuguese',
      autoLearn: true,
      useContext: true,
      useKnowledgeBase: true,
    };
  } catch (error) {
    console.error('Erro ao carregar configurações do bot:', error);
    return {
      model: 'gpt-4',
      temperature: 0.7,
      maxTokens: 150,
      personalityType: 'professional',
      language: 'portuguese',
      autoLearn: true,
      useContext: true,
      useKnowledgeBase: true,
    };
  }
};

// Salva as configurações do bot no localStorage
export const saveBotSettings = (settings: BotSettings): void => {
  try {
    localStorage.setItem(BOT_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Erro ao salvar configurações do bot:', error);
    throw new Error('Erro ao salvar configurações do bot');
  }
};

// Carrega as configurações de captura automática de leads
export const loadAutoLeadCapture = (): AutoLeadCaptureSettings => {
  try {
    const savedSettings = localStorage.getItem(AUTO_LEAD_CAPTURE_KEY);
    if (savedSettings) {
      return JSON.parse(savedSettings);
    }
    return {
      enabled: false,
      autoCreateLead: false,
      captureProfilePic: true,
      notifyNewContacts: true,
    };
  } catch (error) {
    console.error('Erro ao carregar configurações de captura de leads:', error);
    return {
      enabled: false,
      autoCreateLead: false,
      captureProfilePic: true,
      notifyNewContacts: true,
    };
  }
};

// Salva as configurações de captura automática de leads
export const saveAutoLeadCapture = (settings: AutoLeadCaptureSettings): void => {
  try {
    localStorage.setItem(AUTO_LEAD_CAPTURE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Erro ao salvar configurações de captura de leads:', error);
    throw new Error('Erro ao salvar configurações de captura de leads');
  }
};

// Carrega o status da integração de produtos
export const loadProductIntegration = (): boolean => {
  try {
    const savedSetting = localStorage.getItem(PRODUCT_INTEGRATION_KEY);
    if (savedSetting) {
      return JSON.parse(savedSetting);
    }
    return false;
  } catch (error) {
    console.error('Erro ao carregar configuração de integração de produtos:', error);
    return false;
  }
};

// Salva o status da integração de produtos
export const saveProductIntegration = (enabled: boolean): void => {
  try {
    localStorage.setItem(PRODUCT_INTEGRATION_KEY, JSON.stringify(enabled));
  } catch (error) {
    console.error('Erro ao salvar configuração de integração de produtos:', error);
    throw new Error('Erro ao salvar configuração de integração de produtos');
  }
};

// Adiciona uma nova conversa ao histórico
export const addConversation = (newConversation: Omit<Conversation, 'id' | 'timestamp'>): Conversation => {
  const conversations = loadConversationHistory();
  
  const conversation: Conversation = {
    ...newConversation,
    id: `conv_${Date.now()}`,
    timestamp: new Date()
  };
  
  conversations.push(conversation);
  saveConversationHistory(conversations);
  
  return conversation;
};

// Atualiza uma conversa existente
export const updateConversation = (updatedConversation: Conversation): void => {
  const conversations = loadConversationHistory();
  
  const index = conversations.findIndex(conv => conv.id === updatedConversation.id);
  if (index !== -1) {
    conversations[index] = {
      ...updatedConversation,
      timestamp: new Date() // Atualiza o timestamp para a data atual
    };
    saveConversationHistory(conversations);
  }
};

// Adiciona uma mensagem a uma conversa existente
export const addMessageToConversation = (
  conversationId: string, 
  message: Omit<ChatMessage, 'id' | 'timestamp'>
): void => {
  const conversations = loadConversationHistory();
  
  const index = conversations.findIndex(conv => conv.id === conversationId);
  if (index !== -1) {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg_${Date.now()}`,
      timestamp: new Date()
    };
    
    conversations[index].messages.push(newMessage);
    conversations[index].lastMessage = message.text;
    conversations[index].timestamp = new Date();
    
    saveConversationHistory(conversations);
  }
};

// Remove uma conversa do histórico
export const removeConversation = (conversationId: string): void => {
  const conversations = loadConversationHistory();
  
  const filteredConversations = conversations.filter(conv => conv.id !== conversationId);
  if (filteredConversations.length !== conversations.length) {
    saveConversationHistory(filteredConversations);
  }
};

// Altera o status de uma conversa (ativa/fechada)
export const updateConversationStatus = (conversationId: string, status: 'active' | 'closed'): void => {
  const conversations = loadConversationHistory();
  
  const index = conversations.findIndex(conv => conv.id === conversationId);
  if (index !== -1) {
    conversations[index].status = status;
    saveConversationHistory(conversations);
  }
};
